import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SubmissionsPanel from './SubmissionsPanel';
import SubmitContentModal from './SubmitContentModal';
import './JobDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.alooma.mg';

// Digits only -> comma-grouped string for money inputs
const formatMoney = (val) => {
  const digits = String(val ?? '').replace(/[^\d]/g, '');
  if (!digits) return '';
  return parseInt(digits, 10).toLocaleString('en-US');
};

function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [bids, setBids] = useState([]);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showBidModal, setShowBidModal] = useState(false);
  const [bidData, setBidData] = useState({
    amount: '',
    message: '',
    deliveryDays: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Submit content modal: { previous } (previous = submission being revised)
  const [submitTarget, setSubmitTarget] = useState(null);
  const [submissionsReload, setSubmissionsReload] = useState(0);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (!storedUser || !token) {
      navigate('/network');
      return;
    }

    setUser(JSON.parse(storedUser));
    fetchJob(token);
    fetchBids(token);
  }, [jobId, navigate]);

  const fetchJob = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setJob(data.job);
      } else {
        throw new Error(data.message || 'Job not found');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBids = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/jobs/${jobId}/bids`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setBids(data.bids || []);
      }
    } catch (err) {
      console.error('Error fetching bids:', err);
    }
  };

  const handleBidSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/jobs/${jobId}/bids`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: parseInt(bidData.amount),
          message: bidData.message,
          deliveryDays: parseInt(bidData.deliveryDays) || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit bid');
      }

      setSuccess('Bid submitted successfully!');
      setShowBidModal(false);
      setBidData({ amount: '', message: '', deliveryDays: '' });
      fetchBids(token);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptBid = async (bidId) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/bids/${bidId}/accept`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to accept bid');
      }

      setSuccess('Bid accepted! The creator has been notified.');
      fetchJob(token);
      fetchBids(token);
    } catch (err) {
      setError(err.message);
    }
  };

  const getContentTypeLabel = (type) => {
    const types = {
      feed: 'Feed Post',
      story: 'Story',
      reel: 'Reel',
      carousel: 'Carousel',
      mixed: 'Mixed Content',
    };
    return types[type] || type;
  };

  const hasUserBid = bids.some(bid => bid.influencerId === user?.id);
  const acceptedBid = bids.find(bid => bid.influencerId === user?.id && bid.status === 'accepted');

  if (isLoading) {
    return (
      <div className="job-detail-loading">
        <div className="loader"></div>
        <p>Loading job details...</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-detail-error">
        <h2>Job Not Found</h2>
        <p>{error || 'This job posting may have been removed.'}</p>
        <Link to="/network/influencer-dashboard" className="back-link">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  const isBrand = user?.userType === 'brand';
  const isOwner = job.brandId === user?.id;
  const isAgent = user?.userType === 'agent';
  // Who sees the campaign's content submissions here (API scopes the rows)
  const submissionsRole = isBrand && isOwner ? 'brand' : isAgent ? 'agent' : acceptedBid ? 'influencer' : null;

  return (
    <div className="job-detail-page">
      <div className="job-detail-container">
        {/* Back Button */}
        <Link 
          to={isBrand ? '/network/brand-dashboard' : '/network/influencer-dashboard'} 
          className="back-btn"
        >
          ← Back to Dashboard
        </Link>

        {/* Success/Error Messages */}
        {success && <div className="success-message">{success}</div>}
        {error && <div className="error-message">{error}</div>}

        {/* Job Header */}
        <div className="job-detail-header">
          <div className="header-main">
            <span className="job-brand">{job.brandName}</span>
            <h1>{job.title}</h1>
            <div className="job-meta-row">
              <span className="meta-tag">{getContentTypeLabel(job.contentType)}</span>
              <span className="meta-tag">{job.postCount} posts</span>
              {job.storyCount > 0 && <span className="meta-tag">{job.storyCount} stories</span>}
              <span className="meta-date">Posted {new Date(job.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="header-budget">
            <span className="budget-label">Budget</span>
            <span className="budget-value">${job.budget?.toLocaleString()}</span>
          </div>
        </div>

        {/* Job Details */}
        <div className="job-detail-content">
          <div className="content-main">
            <section className="detail-section">
              <h2>Campaign Description</h2>
              <p>{job.description}</p>
            </section>

            {job.requirements && (
              <section className="detail-section">
                <h2>Requirements</h2>
                <p>{job.requirements}</p>
              </section>
            )}

            <section className="detail-section">
              <h2>Details</h2>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Content Type</span>
                  <span className="detail-value">{getContentTypeLabel(job.contentType)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Number of Posts</span>
                  <span className="detail-value">{job.postCount}</span>
                </div>
                {job.storyCount > 0 && (
                  <div className="detail-item">
                    <span className="detail-label">Number of Stories</span>
                    <span className="detail-value">{job.storyCount}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Deadline</span>
                  <span className="detail-value">
                    {job.deadline ? new Date(job.deadline).toLocaleDateString() : 'Flexible'}
                  </span>
                </div>
                {job.minFollowers && (
                  <div className="detail-item">
                    <span className="detail-label">Min. Followers</span>
                    <span className="detail-value">{job.minFollowers.toLocaleString()}</span>
                  </div>
                )}
                {job.niche && (
                  <div className="detail-item">
                    <span className="detail-label">Niche</span>
                    <span className="detail-value" style={{ textTransform: 'capitalize' }}>{job.niche}</span>
                  </div>
                )}
              </div>
            </section>

            {submissionsRole && (
              <section className="detail-section">
                <h2>{submissionsRole === 'influencer' ? 'Your Content Submissions' : 'Content Submissions'}</h2>
                <SubmissionsPanel
                  role={submissionsRole}
                  query={{ jobId }}
                  reloadKey={submissionsReload}
                  emptyText={submissionsRole === 'influencer'
                    ? 'Nothing submitted yet. Use "Submit content" to send your files, links and post caption for review.'
                    : 'No content submitted for this campaign yet.'}
                  onResubmit={(previous) => setSubmitTarget({ previous })}
                />
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="content-sidebar">
            {/* For Influencers: Submit Bid */}
            {!isBrand && job.status === 'open' && (
              <div className="sidebar-card">
                {hasUserBid ? (
                  <>
                    <h3>Bid Submitted</h3>
                    <p>You've already submitted a bid for this job.</p>
                  </>
                ) : (
                  <>
                    <h3>Interested?</h3>
                    <p>Submit a bid to work on this campaign.</p>
                    <button 
                      className="bid-btn"
                      onClick={() => setShowBidModal(true)}
                    >
                      Submit a Bid
                    </button>
                  </>
                )}
              </div>
            )}

            {/* For Creators with an accepted bid: submit content */}
            {acceptedBid && (
              <div className="sidebar-card">
                <h3>You're on this campaign</h3>
                <p>Submit your content (files, links and the post caption) for the brand to review.</p>
                <button className="bid-btn" onClick={() => setSubmitTarget({})}>
                  Submit content
                </button>
              </div>
            )}

            {/* For Brands: View Bids */}
            {isBrand && isOwner && (
              <div className="sidebar-card bids-card">
                <h3>Bids Received ({bids.length})</h3>
                {bids.length === 0 ? (
                  <p className="no-bids">No bids yet. Check back later!</p>
                ) : (
                  <div className="bids-list">
                    {bids.map((bid) => (
                      <div key={bid.id} className={`bid-item ${bid.status}`}>
                        <div className="bid-header">
                          <span className="bidder-name">{bid.influencerName}</span>
                          <span className="bid-amount">${bid.amount?.toLocaleString()}</span>
                        </div>
                        <p className="bid-message">{bid.message?.substring(0, 100)}...</p>
                        <div className="bid-meta">
                          <span>{bid.influencerHandle}</span>
                          {bid.deliveryDays && <span>{bid.deliveryDays} days delivery</span>}
                        </div>
                        {bid.status === 'pending' && (
                          <button 
                            className="accept-btn"
                            onClick={() => handleAcceptBid(bid.id)}
                          >
                            Accept Bid
                          </button>
                        )}
                        {bid.status === 'accepted' && (
                          <span className="status-badge accepted">Accepted</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit Content Modal */}
      {submitTarget && acceptedBid && (
        <SubmitContentModal
          bid={{ ...acceptedBid, jobTitle: job.title, brandName: job.brandName }}
          previous={submitTarget.previous}
          onClose={() => setSubmitTarget(null)}
          onSubmitted={() => setSubmissionsReload((n) => n + 1)}
        />
      )}

      {/* Bid Modal */}
      {showBidModal && (
        <div className="modal-overlay" onClick={() => setShowBidModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowBidModal(false)}>×</button>
            <h2>Submit Your Bid</h2>
            <p>for "{job.title}"</p>
            
            <form onSubmit={handleBidSubmit} className="bid-form">
              <div className="form-group">
                <label htmlFor="amount">Your Bid Amount (USD) *</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="amount"
                    value={formatMoney(bidData.amount)}
                    onChange={(e) => setBidData({ ...bidData, amount: e.target.value.replace(/[^\d]/g, '') })}
                    required
                    placeholder="Enter your rate"
                  />
                </div>
                <span className="form-hint">Budget: ${job.budget?.toLocaleString()}</span>
              </div>

              <div className="form-group">
                <label htmlFor="deliveryDays">Delivery Time (days)</label>
                <input
                  type="number"
                  id="deliveryDays"
                  value={bidData.deliveryDays}
                  onChange={(e) => setBidData({ ...bidData, deliveryDays: e.target.value })}
                  min={1}
                  placeholder="e.g., 7"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Your Proposal *</label>
                <textarea
                  id="message"
                  value={bidData.message}
                  onChange={(e) => setBidData({ ...bidData, message: e.target.value })}
                  required
                  placeholder="Introduce yourself and explain why you're a great fit for this campaign..."
                  rows={5}
                />
              </div>

              <button type="submit" className="submit-bid-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Bid'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobDetail;

